/**
 * WelcomeAgentModal Component
 * Modal de boas-vindas para criar um novo agente
 * Coleta informações obrigatórias antes de permitir edição no canvas
 */

import { useState, useContext } from 'react';
import { Modal, Form, Button, Spinner } from 'react-bootstrap';
import {
  IconRobot,
  IconSparkles,
  IconUser,
  IconBriefcase,
  IconArrowRight,
} from '@tabler/icons-react';
import NotificationsContext from '../../../contexts/NotificationsContext';
import './WelcomeAgentModal.css';

const WelcomeAgentModal = ({ show, onAgentCreated, createAgent, onBack }) => {
  const { setNotify } = useContext(NotificationsContext);
  const [formData, setFormData] = useState({
    name: '',
    role: '',
  });
  const [isCreating, setIsCreating] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'O nome do agente é obrigatório';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'O nome deve ter pelo menos 2 caracteres';
    }

    if (!formData.role.trim()) {
      newErrors.role = 'O cargo/função é obrigatório';
    } else if (formData.role.trim().length < 3) {
      newErrors.role = 'O cargo deve ter pelo menos 3 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsCreating(true);

    try {
      const agentData = {
        name: formData.name.trim(),
        description: `Agente ${formData.role.trim()}`,
        identity_config: {
          display_name: formData.name.trim(),
          role: formData.role.trim(),
          personality_traits: [],
          tone: 'professional',
          gender: 'neutral',
        },
        business_info: {
          business_type: 'other',
        },
        behavior_config: {},
        llm_config: {},
        experience_level: 3, // Level 3 = Canvas/Advanced
      };

      const newAgent = await createAgent(agentData);

      if (newAgent && newAgent.id) {
        // Mostrar notificação de sucesso
        setNotify({
          title: 'Agente criado!',
          message: `O agente "${formData.name.trim()}" foi criado com sucesso. Agora você pode configurar o fluxo.`,
          type: 'success',
          visible: true,
        });

        // Chamar callback para fechar modal e atualizar estado
        onAgentCreated(newAgent);
      }
    } catch (error) {
      console.error('Erro ao criar agente:', error);

      // Mostrar notificação de erro
      setNotify({
        title: 'Erro ao criar agente',
        message: error.response?.data?.message || 'Não foi possível criar o agente. Tente novamente.',
        type: 'error',
        visible: true,
      });

      setErrors({
        submit: error.response?.data?.message || 'Erro ao criar o agente. Tente novamente.',
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Limpar erro do campo ao digitar
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  return (
    <Modal
      show={show}
      centered
      backdrop="static"
      keyboard={false}
      className="wam"
      size="md"
    >
      <Modal.Body className="p-0">
        {/* Header com gradiente */}
        <div className="wam-header">
          <div className="wam-header__icon">
            <IconRobot size={40} stroke={1.5} />
            <div className="wam-header__sparkle">
              <IconSparkles size={20} />
            </div>
          </div>
          <h2 className="wam-header__title">Criar Novo Agente</h2>
          <p className="wam-header__subtitle">
            Vamos configurar as informações básicas do seu agente de IA
          </p>
        </div>

        {/* Formulário */}
        <div className="wam-form">
          <Form onSubmit={handleSubmit}>
            {/* Nome do Agente */}
            <Form.Group className="mb-4">
              <Form.Label className="wam-form__label">
                <IconUser size={18} className="me-2" />
                Nome do Agente
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Ex: Ana, Dr. Carlos, Assistente Virtual"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className={`wam-form__input ${errors.name ? 'is-invalid' : ''}`}
                autoFocus
                maxLength={50}
              />
              {errors.name && (
                <Form.Control.Feedback type="invalid">
                  {errors.name}
                </Form.Control.Feedback>
              )}
              <Form.Text className="text-muted">
                Como o agente vai se apresentar aos clientes
              </Form.Text>
            </Form.Group>

            {/* Cargo/Função */}
            <Form.Group className="mb-4">
              <Form.Label className="wam-form__label">
                <IconBriefcase size={18} className="me-2" />
                Cargo / Função
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Ex: Assistente Virtual, Atendente, Recepcionista"
                value={formData.role}
                onChange={(e) => handleChange('role', e.target.value)}
                className={`wam-form__input ${errors.role ? 'is-invalid' : ''}`}
                maxLength={50}
              />
              {errors.role && (
                <Form.Control.Feedback type="invalid">
                  {errors.role}
                </Form.Control.Feedback>
              )}
              <Form.Text className="text-muted">
                A função que o agente desempenhará no atendimento
              </Form.Text>
            </Form.Group>

            {/* Erro geral */}
            {errors.submit && (
              <div className="alert alert-danger mb-4">
                {errors.submit}
              </div>
            )}

            {/* Botão de criar */}
            <Button
              type="submit"
              className="wam-form__submit"
              disabled={isCreating}
            >
              {isCreating ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Criando agente...
                </>
              ) : (
                <>
                  Criar e Continuar
                  <IconArrowRight size={20} className="ms-2" />
                </>
              )}
            </Button>
          </Form>

          {/* Voltar para escolha */}
          {onBack && (
            <div className="text-center mt-3 mb-2">
              <button
                type="button"
                className="btn btn-link text-muted text-decoration-none"
                onClick={onBack}
                disabled={isCreating}
              >
                Voltar para opções de criação
              </button>
            </div>
          )}

          {/* Dica */}
          <div className="wam-tip">
            <IconSparkles size={16} className="me-2" />
            <span>Você poderá personalizar mais detalhes depois nas configurações avançadas</span>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default WelcomeAgentModal;
